import React, { useState, useEffect } from 'react';
import { Product, Ingredient, Recipe } from '../types/interfaces';

export type RecipeSavePayload = {
  name: string;
  productId: number | string;
  ingredients: Ingredient[];
  quantity: number;
  unit: string;
};

interface RecipeModalProps {
  show: boolean;
  onClose: () => void;
  onSave: (recipe: RecipeSavePayload) => void;
  products: Product[];
  items: { name: string; type: string; enabled: number }[];
  defaultProductId?: number | string;
  /** When set, modal edits this recipe instead of creating. */
  initialRecipe?: Recipe | null;
}

const emptyForm = (defaultProductId?: number | string) => ({
  name: '',
  productId: defaultProductId as number | string | undefined,
  ingredients: [{ itemName: '', quantity: 0, unit: 'lbs' }] as Ingredient[],
  quantity: 0,
  unit: 'barrels',
});

const RecipeModal: React.FC<RecipeModalProps> = ({
  show,
  onClose,
  onSave,
  products,
  items,
  defaultProductId,
  initialRecipe = null,
}) => {
  const [recipe, setRecipe] = useState(emptyForm(defaultProductId));
  const [error, setError] = useState<string | null>(null);

  const isSingleProduct = products.length === 1 && defaultProductId !== undefined;
  const isEdit = !!(initialRecipe && initialRecipe.id);

  useEffect(() => {
    if (!show) return;
    setError(null);
    if (initialRecipe && initialRecipe.id) {
      setRecipe({
        name: initialRecipe.name || '',
        productId: initialRecipe.productId ?? defaultProductId,
        ingredients:
          initialRecipe.ingredients && initialRecipe.ingredients.length
            ? initialRecipe.ingredients.map((ing) => ({
                itemName: ing.itemName,
                quantity: Number(ing.quantity) || 0,
                unit: ing.unit || 'lbs',
              }))
            : [{ itemName: '', quantity: 0, unit: 'lbs' }],
        quantity: Number(initialRecipe.quantity) || 0,
        unit: initialRecipe.unit || 'barrels',
      });
    } else {
      setRecipe(emptyForm(defaultProductId));
    }
  }, [show, initialRecipe, defaultProductId]);

  useEffect(() => {
    if (isSingleProduct && defaultProductId && show) {
      setRecipe((prev) => ({ ...prev, productId: defaultProductId }));
    }
  }, [defaultProductId, isSingleProduct, show]);

  const addIngredient = () => {
    setRecipe({
      ...recipe,
      ingredients: [...recipe.ingredients, { itemName: '', quantity: 0, unit: 'lbs' }],
    });
  };

  const removeIngredient = (index: number) => {
    setRecipe({
      ...recipe,
      ingredients: recipe.ingredients.filter((_, i) => i !== index),
    });
  };

  const updateIngredient = (index: number, field: keyof Ingredient, value: string | number) => {
    const updatedIngredients = [...recipe.ingredients];
    updatedIngredients[index] = { ...updatedIngredients[index], [field]: value };
    setRecipe({ ...recipe, ingredients: updatedIngredients });
  };

  const handleSubmit = () => {
    if (
      !recipe.name ||
      !recipe.productId ||
      recipe.quantity <= 0 ||
      !recipe.unit ||
      recipe.ingredients.some((ing) => !ing.itemName || ing.quantity <= 0 || !ing.unit)
    ) {
      setError('Recipe name, product, valid quantity, unit, and ingredients are required');
      return;
    }
    onSave({
      name: recipe.name,
      productId: recipe.productId,
      ingredients: recipe.ingredients,
      quantity: recipe.quantity,
      unit: recipe.unit,
    });
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2100 }}>
      <div className="modal-dialog modal-content" style={{ maxWidth: '500px', margin: '0 auto' }}>
        <div className="modal-header">
          <h5 className="modal-title" style={{ color: '#555555' }} data-testid="recipe-modal-title">
            {isEdit ? 'Edit Recipe' : 'Create New Recipe'}
          </h5>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-danger">{error}</div>}
          <div className="recipe-form">
            <label className="form-label" style={{ fontWeight: 'bold', color: '#555555' }}>
              Recipe Name (required):
              <input
                type="text"
                value={recipe.name}
                onChange={(e) => setRecipe({ ...recipe, name: e.target.value })}
                placeholder="Enter recipe name"
                className="form-control"
                data-testid="recipe-name-input"
              />
            </label>
            {isSingleProduct ? (
              <label className="form-label" style={{ fontWeight: 'bold', color: '#555555' }}>
                Product:
                <input
                  type="text"
                  value={products[0]?.name || 'Unknown Product'}
                  disabled
                  className="form-control"
                />
              </label>
            ) : (
              <label className="form-label" style={{ fontWeight: 'bold', color: '#555555' }}>
                Product (required):
                <select
                  value={recipe.productId || ''}
                  onChange={(e) =>
                    setRecipe({ ...recipe, productId: parseInt(e.target.value, 10) || undefined })
                  }
                  className="form-control"
                >
                  <option value="">Select Product</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="form-label" style={{ fontWeight: 'bold', color: '#555555' }}>
              Recipe Quantity (required):
              <input
                type="number"
                value={recipe.quantity || ''}
                onChange={(e) => setRecipe({ ...recipe, quantity: parseFloat(e.target.value) || 0 })}
                placeholder="Enter quantity (e.g., 10)"
                step="0.01"
                min="0"
                className="form-control"
                data-testid="recipe-quantity-input"
              />
            </label>
            <label className="form-label" style={{ fontWeight: 'bold', color: '#555555' }}>
              Unit (required):
              <select
                value={recipe.unit}
                onChange={(e) => setRecipe({ ...recipe, unit: e.target.value })}
                className="form-control"
                data-testid="recipe-unit-select"
              >
                <option value="barrels">Barrels</option>
                <option value="gallons">Gallons</option>
                <option value="liters">Liters</option>
              </select>
            </label>
            <label className="form-label" style={{ fontWeight: 'bold', color: '#555555' }}>
              Ingredients (required):
              {recipe.ingredients.map((ingredient, index) => (
                <div key={index} className="d-flex gap-2 mb-2 align-items-center">
                  <select
                    value={ingredient.itemName}
                    onChange={(e) => updateIngredient(index, 'itemName', e.target.value)}
                    className="form-control"
                    data-testid={`recipe-ingredient-item-${index}`}
                  >
                    <option value="">Select Item</option>
                    {items
                      .filter((item) => item.enabled)
                      .map((item) => (
                        <option key={item.name} value={item.name}>
                          {item.name}
                        </option>
                      ))}
                  </select>
                  <input
                    type="number"
                    value={ingredient.quantity || ''}
                    onChange={(e) =>
                      updateIngredient(index, 'quantity', parseFloat(e.target.value) || 0)
                    }
                    placeholder="Quantity"
                    step="0.01"
                    min="0"
                    className="form-control"
                    style={{ width: '100px' }}
                    data-testid={`recipe-ingredient-qty-${index}`}
                  />
                  <select
                    value={ingredient.unit}
                    onChange={(e) => updateIngredient(index, 'unit', e.target.value)}
                    className="form-control"
                    style={{ width: '100px' }}
                  >
                    <option value="lbs">lbs</option>
                    <option value="kg">kg</option>
                    <option value="oz">oz</option>
                    <option value="gal">gal</option>
                    <option value="l">l</option>
                  </select>
                  <button
                    onClick={() => removeIngredient(index)}
                    className="btn btn-danger"
                    style={{ padding: '8px 12px' }}
                    type="button"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button onClick={addIngredient} className="btn btn-primary mt-2" type="button">
                Add Ingredient
              </button>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={handleSubmit} className="btn btn-primary" data-testid="recipe-save-btn">
            {isEdit ? 'Save Changes' : 'Create'}
          </button>
          <button onClick={onClose} className="btn btn-danger" type="button">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecipeModal;
